import React, { useMemo, useState, useEffect, useCallback } from 'react';
import Step1Destination from './steps/Step1Destination';
import useCities from "../../../../hooks/useCities";
import Model from "@tripian/model";
import moment from "moment/moment";
import useTranslate from "../../../../hooks/useTranslate";
import { useSearchParams, useNavigate } from "react-router-dom";
import Step2Destination from "./steps/Step2Destination";
import Step3Destination from "./steps/Step3Destination";
import Step4Destination from "./steps/Step4Destination";
import Step5Destination from "./steps/Step5Destination";
import Step6Destination from "./steps/Step6Destination";
import Step7Destination from "./steps/Step7Destination";
import Step8Destination from "./steps/Step8Destination";
import useQuestionTrip from "../../../../hooks/useQuestionTrip";
import useTimelineTrip from "../../../../hooks/useTimelineTrip";
import { PageLoading } from "@tripian/react";
import { TIMELINE_PLAN, TRIP_PLAN } from "../../../../constants/ROUTER_PATH_TITLE";

interface StepFormProps {
    currentStep: number;
    onNext: () => void;
    onBack: () => void;
    isFirstStep: boolean;
    isLastStep: boolean;
    editMode?: boolean;
    editTripHash?: string;
    editSegment?: Model.TimelineTripProfileSegment;
    editSegmentIndex?: number;
    existingSegmentCount?: number;
}

interface BookingData {
    tourName: string;
    tourImage: string;
    travelDate: string;
    travelHour: string;
    duration: number; // in minutes
    coordinate?: { lat: number; lng: number };
}

// Helper function to create daily segments from a date range
const createDailySegments = (
    cityId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    adults: number,
    children: number,
    coordinate: { lat: number; lng: number } | null = null,
    dayOffset: number = 0 // Offset for day numbering (e.g., if trip already has 3 days, new segment starts at Day 4)
): Model.TimelineTripProfileSegment[] => {
    const segments: Model.TimelineTripProfileSegment[] = [];

    // Get start and end times from user selection
    const startTime = startDate.format("HH:mm");
    const endTime = endDate.format("HH:mm");

    // Calculate number of days
    const startDay = moment(startDate).startOf('day');
    const endDay = moment(endDate).startOf('day');
    const numberOfDays = endDay.diff(startDay, 'days') + 1;

    for (let i = 0; i < numberOfDays; i++) {
        const currentDay = moment(startDay).add(i, 'days');
        const dayNumber = dayOffset + i + 1; // Day numbering with offset
        const daySegment: Model.TimelineTripProfileSegment = {
            distinctPlan: true,
            cityId: cityId,
            available: true,
            title: `Day ${dayNumber}`,
            description: "",
            startDate: currentDay.format("YYYY-MM-DD") + " " + startTime,
            endDate: currentDay.format("YYYY-MM-DD") + " " + endTime,
            coordinate: coordinate,
            children: children,
            pets: 0,
            adults: adults,
            answerIds: [],
            doNotRecommend: [],
            excludePoiIds: [],
            includePoiIds: [],
            accommodation: null,
            doNotGenerate: 0,
        };
        segments.push(daySegment);
    }

    return segments;
};

// Helper function to build initial segments
const buildInitialSegments = (
    cityId: number,
    arrivalDate: string,
    departureDate: string,
    adults: number,
    children: number,
    bookingData?: BookingData
): Model.TimelineTripProfileSegment[] => {
    const segments: Model.TimelineTripProfileSegment[] = [];

    if (bookingData) {
        // Booking segment (available: false)
        const bookingStartDate = moment(`${bookingData.travelDate} ${bookingData.travelHour}`, "YYYY-MM-DD HH:mm");
        const bookingEndDate = moment(bookingStartDate).add(bookingData.duration, 'minutes');

        const bookingSegment: Model.TimelineTripProfileSegment = {
            distinctPlan: true,
            cityId: cityId,
            available: false,
            title: bookingData.tourName,
            description: `Booked tour: ${bookingData.tourName}`,
            startDate: bookingStartDate.format("YYYY-MM-DD HH:mm"),
            endDate: bookingEndDate.format("YYYY-MM-DD HH:mm"),
            coordinate: bookingData.coordinate || null,
            children: children,
            pets: 0,
            adults: adults,
            answerIds: [],
            doNotRecommend: [],
            excludePoiIds: [],
            includePoiIds: [],
            accommodation: null,
            doNotGenerate: 0,
        };
        segments.push(bookingSegment);

        // Create daily segments after booking (available: true)
        const generalEndDate = moment(departureDate);

        if (bookingEndDate.isBefore(generalEndDate)) {
            const dailySegments = createDailySegments(
                cityId,
                bookingEndDate,
                generalEndDate,
                adults,
                children,
                bookingData.coordinate || null
            );
            segments.push(...dailySegments);
        }
    } else {
        // No booking - create daily segments (available: true)
        const dailySegments = createDailySegments(
            cityId,
            moment(arrivalDate),
            moment(departureDate),
            adults,
            children
        );
        segments.push(...dailySegments);
    }

    return segments;
};

const StepForm: React.FC<StepFormProps> = ({
    currentStep,
    onNext,
    onBack,
    isFirstStep,
    isLastStep,
    editMode = false,
    editTripHash,
    editSegment,
    editSegmentIndex,
    existingSegmentCount = 0,
}) => {
    const [isStep4Valid, setIsStep4Valid] = useState(false);
    const [isStep5Valid, setIsStep5Valid] = useState(false);
    const [isStep6Valid, setIsStep6Valid] = useState(false);
    const [isStep7Valid, setIsStep7Valid] = useState(false);
    const [isStep8Valid, setIsStep8Valid] = useState(false);
    const [isCreatingTrip, setIsCreatingTrip] = useState(false);
    const { cities, loadingCities } = useCities();
    const { t } = useTranslate();
    const { timelineTripAdd, timelineTripUpdate, loadingTimelineTripAdd, loadingTimelineTripUpdate } = useTimelineTrip();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const cityId = searchParams.get("cityId");
    const arrivalDate = searchParams.get("arrivalDate");
    const departureDate = searchParams.get("departureDate");
    const adults = searchParams.get("adults");
    const childrenParam = searchParams.get("children");
    const bookingParam = searchParams.get("booking");
    const accommodationLat = searchParams.get("lat");
    const accommodationLng = searchParams.get("lng");
    // Parse booking data from URL if exists
    const bookingData: BookingData | undefined = useMemo(() => {
        if (bookingParam) {
            try {
                return JSON.parse(decodeURIComponent(bookingParam));
            } catch (e) {
                console.error("Failed to parse booking data:", e);
                return undefined;
            }
        }
        return undefined;
    }, [bookingParam]);

    // Default dates
    const defaultArrival = arrivalDate || moment().add(1, "days").set({ hour: 9, minute: 0 }).format("YYYY-MM-DDTHH:mm:ss");
    const defaultDeparture = departureDate || moment().add(4, "days").set({ hour: 18, minute: 0 }).format("YYYY-MM-DDTHH:mm:ss");
    const initialCityId = cityId ? parseInt(cityId) : 0;
    const initialAdults = adults ? parseInt(adults) : 1;
    const initialChildren = childrenParam ? parseInt(childrenParam) : 0;

    // Determine if this is create segment mode (editMode but no editSegment)
    const isCreateSegmentMode = editMode && editTripHash && editSegmentIndex === undefined;

    // Initialize tripProfile as TimelineTripProfile
    // In edit mode with segment, use editSegment data
    // In create segment mode, use empty/default values with correct day offset
    const [tripProfile, setTripProfile] = useState<Model.TimelineTripProfile>(() => {
        if (editMode && editSegment) {
            // Edit existing segment
            return {
                cityId: editSegment.cityId,
                adults: editSegment.adults,
                children: editSegment.children,
                pets: editSegment.pets,
                answerIds: editSegment.answerIds || [],
                doNotRecommend: editSegment.doNotRecommend || null,
                excludePoiIds: editSegment.excludePoiIds || [],
                segments: [{
                    ...editSegment,
                    startDate: moment(editSegment.startDate).format("YYYY-MM-DD HH:mm"),
                    endDate: moment(editSegment.endDate).format("YYYY-MM-DD HH:mm"),
                }],
            };
        }

        // Create new trip OR create new segment - use default empty values
        // For create segment mode, use existingSegmentCount as day offset
        const dayOffset = editMode && editTripHash && editSegmentIndex === undefined ? existingSegmentCount : 0;

        // Build segments with correct day numbering
        const dailySegments = createDailySegments(
            initialCityId,
            moment(defaultArrival),
            moment(defaultDeparture),
            initialAdults,
            initialChildren,
            null,
            dayOffset
        );

        return {
            cityId: initialCityId,
            adults: initialAdults,
            children: initialChildren,
            pets: 0,
            answerIds: [],
            doNotRecommend: null,
            excludePoiIds: [],
            segments: bookingData ? buildInitialSegments(initialCityId, defaultArrival, defaultDeparture, initialAdults, initialChildren, bookingData) : dailySegments,
        };
    });

    // Form state for dates (used in Step1)
    // For booking: use available segment's dates (starts after booking ends)
    // For edit mode: use editSegment's dates
    // Otherwise: use default dates
    const getInitialDates = () => {
        if (editMode && editSegment) {
            return {
                arrival: moment(editSegment.startDate).format("YYYY-MM-DD HH:mm"),
                departure: moment(editSegment.endDate).format("YYYY-MM-DD HH:mm")
            };
        }
        // Find available segments and get startDate from first, endDate from last
        const availableSegments = tripProfile.segments.filter(s => s.available);
        if (availableSegments.length > 0) {
            const firstAvailable = availableSegments[0];
            const lastAvailable = availableSegments[availableSegments.length - 1];
            return {
                arrival: firstAvailable.startDate,
                departure: lastAvailable.endDate
            };
        }
        return {
            arrival: defaultArrival,
            departure: defaultDeparture
        };
    };
    const initialDates = getInitialDates();
    const [arrivalDatetime, setArrivalDatetime] = useState(initialDates.arrival);
    const [departureDatetime, setDepartureDatetime] = useState(initialDates.departure);

    // Log tripProfile on every change
    useEffect(() => {
    }, [tripProfile]);

    const isStep1Valid = useMemo(() => {
        return tripProfile.cityId !== 0 && arrivalDatetime && departureDatetime;
    }, [tripProfile.cityId, arrivalDatetime, departureDatetime]);

    const isStep3Changed = useMemo(() => {
        return tripProfile.adults !== 1 || tripProfile.children !== 0;
    }, [tripProfile.adults, tripProfile.children]);

    const { questionsTrip, loadingQuestionsTrip } = useQuestionTrip();

    const [bound, setBound] = useState<google.maps.LatLngBounds | undefined>();
    const setBoundMemo = useCallback((newBound?: google.maps.LatLngBounds | undefined) => setBound(newBound), []);
    useEffect(() => {
        if (!cities) return;
        const currentCity = cities.find((city) => city.id === tripProfile.cityId);
        if (currentCity) {
            const getTopParentCity = (c: Model.City): Model.City => {
                const parentCity = cities.find((city) => city.id === c.parentLocationId);
                if (parentCity === undefined) return c;
                return getTopParentCity(parentCity);
            };

            const topParentCity = getTopParentCity(currentCity);
            const { boundary } = topParentCity;

            const newBoundary = new google.maps.LatLngBounds(new google.maps.LatLng(boundary[0], boundary[2]), new google.maps.LatLng(boundary[1], boundary[3]));
            setBound(newBoundary);
        }
    }, [cities, setBound, tripProfile.cityId]);

    // Fetch accommodation from URL coordinates using Google Places
    const [accommodationFetched, setAccommodationFetched] = useState(false);
    useEffect(() => {
        if (accommodationFetched || editMode) return;
        if (!accommodationLat || !accommodationLng) return;

        const lat = parseFloat(accommodationLat);
        const lng = parseFloat(accommodationLng);
        if (isNaN(lat) || isNaN(lng)) return;

        // Use Google Geocoder to get place info from coordinates
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            { location: { lat, lng } },
            (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
                    const place = results[0];
                    const accommodation: Model.Accommodation = {
                        name: place.formatted_address?.split(',')[0] || 'Selected Location',
                        provider: 'Google',
                        imageUrl: '',
                        refID: place.place_id || null,
                        address: place.formatted_address || '',
                        coordinate: { lat, lng }
                    };

                    // Update tripProfile with accommodation
                    setTripProfile(prev => ({
                        ...prev,
                        segments: prev.segments.map(segment => {
                            if (segment.available) {
                                return {
                                    ...segment,
                                    accommodation,
                                    coordinate: { lat, lng },
                                };
                            }
                            return segment;
                        }),
                    }));
                    setAccommodationFetched(true);
                }
            }
        );
    }, [accommodationLat, accommodationLng, accommodationFetched, editMode]);

    const handleContinueClick = async () => {
        if (isLastStep) {
            await createTripAndNavigate();
        } else {
            onNext();
        }
    };

    const createTripAndNavigate = async () => {
        try {
            setIsCreatingTrip(true);

            // Update only available segments with form data (booking segments stay unchanged)
            const updatedSegments = tripProfile.segments.map(segment => {
                if (segment.available) {
                    return {
                        ...segment,
                        answerIds: tripProfile.answerIds,
                        adults: tripProfile.adults,
                        children: tripProfile.children,
                    };
                }
                // Booking segment (available: false) stays unchanged
                return segment;
            });

            const finalTripProfile: Model.TimelineTripProfile = {
                ...tripProfile,
                segments: updatedSegments,
            };

            if (editMode && editTripHash) {
                // Edit or Create segment mode - call update API
                const targetSegment = updatedSegments[0];
                if (targetSegment) {
                    // Build segment request object
                    const segmentRequest: any = {
                        distinctPlan: targetSegment.distinctPlan,
                        cityId: targetSegment.cityId,
                        available: targetSegment.available,
                        title: targetSegment.title,
                        description: targetSegment.description,
                        startDate: targetSegment.startDate,
                        endDate: targetSegment.endDate,
                        children: targetSegment.children,
                        pets: targetSegment.pets,
                        adults: targetSegment.adults,
                        answerIds: targetSegment.answerIds,
                        doNotRecommend: targetSegment.doNotRecommend,
                        excludePoiIds: targetSegment.excludePoiIds,
                        includePoiIds: targetSegment.includePoiIds,
                        accommodation: targetSegment.accommodation,
                        doNotGenerate: targetSegment.doNotGenerate,
                    };

                    // Always include coordinate (null resets it on backend)
                    segmentRequest.coordinate = targetSegment.coordinate;

                    // Add segmentIndex only for edit mode (not create segment mode)
                    if (editSegmentIndex !== undefined) {
                        segmentRequest.segmentIndex = editSegmentIndex;
                    }

                    await timelineTripUpdate(editTripHash, segmentRequest);

                    // Clear all empty slot data for this trip from localStorage
                    const keysToRemove: string[] = [];
                    for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        if (key && key.startsWith(`timeline_deleted_slots_${editTripHash}_`)) {
                            keysToRemove.push(key);
                        }
                    }
                    keysToRemove.forEach(key => localStorage.removeItem(key));

                    // Navigate to detail page with the hash
                    navigate(`${TIMELINE_PLAN.PATH}/${editTripHash}/0`);
                }
            } else {
                // Create new trip mode - call add API
                const trip = await timelineTripAdd(finalTripProfile);
                navigate(`${TIMELINE_PLAN.PATH}/${trip.tripHash}/0`);
            }
        } catch (error) {
            console.error("Trip creation/update error:", error);
            if (!editMode) {
                onNext();
            }
        } finally {
            setIsCreatingTrip(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1Destination
                    selectedDestinationId={tripProfile.cityId}
                    onSelect={(selectedOption) => {
                        setTripProfile(prev => {
                            // Check if city is actually changing
                            const isCityChanging = prev.cityId !== selectedOption.id;

                            // Get selected city's coordinate from cities list
                            const selectedCity = cities?.find(c => c.id === selectedOption.id);
                            const cityCoordinate = selectedCity?.coordinate ? {
                                lat: selectedCity.coordinate.lat,
                                lng: selectedCity.coordinate.lng
                            } : null;

                            return {
                                ...prev,
                                cityId: selectedOption.id,
                                segments: prev.segments.map((seg) => {
                                    if (seg.available) {
                                        return {
                                            ...seg,
                                            cityId: selectedOption.id,
                                            // Update coordinate to city center and reset accommodation when city changes
                                            ...(isCityChanging ? { coordinate: cityCoordinate, accommodation: null } : {}),
                                        };
                                    }
                                    return seg;
                                })
                            };
                        });
                    }}
                    arrivalDatetime={arrivalDatetime}
                    departureDatetime={departureDatetime}
                    onDateChange={(arrival, departure) => {
                        setArrivalDatetime(arrival);
                        setDepartureDatetime(departure);

                        if (editMode && editSegment) {
                            // Edit existing segment mode - just update the segment's dates, keep title and other data
                            setTripProfile(prev => ({
                                ...prev,
                                segments: prev.segments.map((seg) => {
                                    if (seg.available) {
                                        return {
                                            ...seg,
                                            startDate: arrival,
                                            endDate: departure
                                        };
                                    }
                                    return seg;
                                })
                            }));
                        } else {
                            // Create mode (new trip or new segment) - recreate daily segments with new dates
                            // Keep booking segments (available=false) unchanged
                            setTripProfile(prev => {
                                // Get booking segments to preserve
                                const bookingSegments = prev.segments.filter(seg => !seg.available);

                                // Get data from first available segment for new segments
                                const firstAvailable = prev.segments.find(seg => seg.available);
                                const coordinate = firstAvailable?.coordinate || null;

                                // Use existingSegmentCount as dayOffset for create segment mode
                                const dayOffset = isCreateSegmentMode ? existingSegmentCount : 0;

                                // Create new daily segments with correct day numbering
                                const newDailySegments = createDailySegments(
                                    prev.cityId,
                                    moment(arrival),
                                    moment(departure),
                                    prev.adults,
                                    prev.children,
                                    coordinate,
                                    dayOffset
                                );

                                return {
                                    ...prev,
                                    segments: [...bookingSegments, ...newDailySegments]
                                };
                            });
                        }
                    }}
                    maxDays={editMode && editTripHash ? 2 : undefined}
                />;
            case 2:
                return <Step2Destination
                    tripProfile={{
                        ...tripProfile,
                        // Map accommodation from first available segment for display
                        accommodation: tripProfile.segments.find(s => s.available)?.accommodation || undefined,
                    } as any}
                    onUpdateTripProfile={(newProfile: any) => {
                        // Update accommodation in available segments only
                        setTripProfile(prev => ({
                            ...prev,
                            segments: prev.segments.map(segment => {
                                if (segment.available) {
                                    return {
                                        ...segment,
                                        accommodation: newProfile.accommodation || null,
                                        // Also update coordinate from accommodation if available
                                        coordinate: newProfile.accommodation?.coordinate || segment.coordinate,
                                    };
                                }
                                return segment;
                            }),
                        }));
                    }}
                    bound={bound}
                />
            case 3:
                return <Step3Destination
                    numberOfAdults={tripProfile.adults}
                    numberOfChildren={tripProfile.children}
                    onUpdateTravelers={(newAdults, newChildren) => {
                        setTripProfile(prev => ({
                            ...prev,
                            adults: newAdults,
                            children: newChildren
                        }));
                    }}
                />
            case 4:
                // Get all answer IDs that belong to question 6 for initialSelectedIds
                const question6 = questionsTrip?.find(q => q.id === 6);
                const question6AnswerIds: number[] = [];
                question6?.answers.forEach(a => {
                    question6AnswerIds.push(a.id);
                    a.subAnswers?.forEach(sa => question6AnswerIds.push(sa.id));
                });
                const initialStep4Ids = tripProfile.answerIds.filter(id => question6AnswerIds.includes(id));
                return <Step4Destination
                    onStepValidation={setIsStep4Valid}
                    onUpdateTripProfile={(selectedIds: number[]) => {
                        const questionId = 6; // Step4'teki soru ID'si
                        setTripProfile(prev => ({
                            ...prev,
                            answerIds: [...prev.answerIds.filter((id: number) => !questionsTrip?.find(q => q.id === questionId)?.answers.some(a => a.id === id || a.subAnswers?.some(sa => sa.id === id))), ...selectedIds]
                        }));
                    }}
                    initialSelectedIds={initialStep4Ids}
                />
            case 5:
                // Question 19 answer IDs for Step5
                const question19 = questionsTrip?.find(q => q.id === 19);
                const question19AnswerIds = question19?.answers.map(a => a.id) || [];
                const initialStep5Id = tripProfile.answerIds.find(id => question19AnswerIds.includes(id));

                return <Step5Destination
                    onStepValidation={setIsStep5Valid}
                    onUpdateTripProfile={(answerId: number | null) => {
                        if (answerId) {
                            setTripProfile(prev => ({
                                ...prev,
                                answerIds: [...prev.answerIds.filter((id: number) => !question19AnswerIds.includes(id)), answerId]
                            }));
                        }
                    }}
                    initialSelectedId={initialStep5Id}
                />
            case 6:
                // Budget answer IDs for Step6 (501, 502, 503)
                const budgetAnswerIds = [501, 502, 503];
                const initialStep6Id = tripProfile.answerIds.find(id => budgetAnswerIds.includes(id));

                return <Step6Destination
                    onStepValidation={setIsStep6Valid}
                    onUpdateTripProfile={(answerId: number | null) => {
                        if (answerId) {
                            setTripProfile(prev => ({
                                ...prev,
                                answerIds: [...prev.answerIds.filter((id: number) => !budgetAnswerIds.includes(id)), answerId]
                            }));
                        }
                    }}
                    initialSelectedId={initialStep6Id}
                />
            case 7:
                // Question 11 answer IDs for Step7
                const question11 = questionsTrip?.find(q => q.id === 11);
                const question11AnswerIds = question11?.answers.map(a => a.id) || [];
                const initialStep7Id = tripProfile.answerIds.find(id => question11AnswerIds.includes(id));

                return <Step7Destination
                    onStepValidation={setIsStep7Valid}
                    onUpdateTripProfile={(answerId: number | null) => {
                        if (answerId) {
                            setTripProfile(prev => ({
                                ...prev,
                                answerIds: [...prev.answerIds.filter((id: number) => !question11AnswerIds.includes(id)), answerId]
                            }));
                        }
                    }}
                    initialSelectedId={initialStep7Id}
                />
            case 8:
                // Question 1111 answer IDs for Step8
                const question1111 = questionsTrip?.find(q => q.id === 1111);
                const question1111AnswerIds = question1111?.answers.map(a => a.id) || [];
                const initialStep8Id = tripProfile.answerIds.find(id => question1111AnswerIds.includes(id));

                return <Step8Destination
                    onStepValidation={setIsStep8Valid}
                    onUpdateTripProfile={(answerId: number | null) => {
                        if (answerId) {
                            setTripProfile(prev => ({
                                ...prev,
                                answerIds: [...prev.answerIds.filter((id: number) => !question1111AnswerIds.includes(id)), answerId]
                            }));
                        }
                    }}
                    initialSelectedId={initialStep8Id}
                />
            default:
                return <div>Step {currentStep} content will be added</div>;
        }
    };

    return (
        <>
            {isCreatingTrip && <PageLoading />}

            <div className="bg-white rounded-lg p-6 shadow-sm">
                {renderStep()}

                <div className={`flex mt-8 ${isFirstStep && !editMode ? 'justify-center' : 'justify-between'}`}>
                    <div className="flex gap-3">
                        {editMode && (
                            <button
                                onClick={() => navigate(-1)}
                                className="px-6 py-2 border border-red-300 rounded-full text-red-600 hover:bg-red-50"
                            >
                                {t('trips.cloneTrip.cancel')}
                            </button>
                        )}
                        {!isFirstStep && (
                            <button
                                onClick={onBack}
                                className="px-6 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50"
                            >
                                {t('trips.cloneTrip.back')}
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleContinueClick}
                        disabled={(currentStep === 1 && !isStep1Valid) || (currentStep === 4 && !isStep4Valid) || isCreatingTrip}
                        className={`pd-step-form-c-btn font-14-h rounded-full ${(currentStep === 1 && !isStep1Valid) || (currentStep === 4 && !isStep4Valid) || isCreatingTrip
                                ? 'bg-gray-300 cursor-not-allowed'

                                : 'bg-primary-color text-white hover:bg-primary-color/90'
                            }`}
                    >
                        {isCreatingTrip ? t('valai.creatingTrip') : (currentStep === 3 && !isStep3Changed) || (currentStep === 5 && !isStep5Valid) || (currentStep === 6 && !isStep6Valid) || (currentStep === 7 && !isStep7Valid) || (currentStep === 8 && !isStep8Valid) ? t('valai.skip') : t('trips.createNewTrip.form.continue')}
                    </button>
                </div>
            </div>
        </>
    );
};

export default StepForm;
